// utils
import { fetcher, endpoints, poster, formPoster, patcher } from '../utils/axios';

/**
 * Fetches the current user from the server.
 * @returns {Promise} - A promise that resolves with the user object.
 */
export async function getUser() {
    const URL = endpoints.user.root;
    return await fetcher([URL, { params: null }])
}

/**
 * Fetches the user profile from the server.
 * @param {Object} payload - The payload containing the user ID.
 * @returns {Promise} - A promise that resolves with the user profile object.
 */
export async function getUserProfile(payload) {
    const URL = endpoints.user.getUserProfile;
    return await fetcher([URL, payload])
}

/**
 * Updates the current user's basic profile information.
 * @param {Object} payload - The object containing the basic profile information to update.
 * @returns {Promise} - A promise that resolves with the basic profile update response.
 */
export async function updateBasicProfile(payload) {
    const URL = endpoints.user.updateBasicProfile;
    return await poster([URL, payload])
}

/**
 * Updates the personal details of the current user.
 * @param {Object} payload - The object containing the personal details to update.
 * @returns {Promise} - A promise that resolves with the personal details update response.
 */

export async function updatePersonalDetails(payload) {
    const URL = endpoints.user.updatePersonalDetails;
    return await poster([URL, payload])
}


/**
 * Adds a LinkedIn profile to the user's profile.
 * @param {Object} payload - The object containing the LinkedIn profile information to add.
 * @returns {Promise} - A promise that resolves with the LinkedIn profile add response.
 */
export async function addLinkedInProfile(payload) {
    const URL = endpoints.user.addLinkedInProfile;
    return await poster([URL, payload])
}

/**
 * Retrieves the basic profile details of a user.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the user profile details.
 */

export async function getBasicProfileDetails(payload) {
    const URL = endpoints.user.getBasicProfileDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the key focus areas of a user.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the key focus areas of the user.
 */
export async function getKeyFocusArea(payload) {
    const URL = endpoints.user.getKeyFocusArea;
    return await fetcher([URL, { params: payload }])
}

/**
 * Adds a candidate resume to the server.
 * @param {Object} payload - The payload containing the resume information to add.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server.
 */
export async function addCandidateResume(payload, headers) {
    const URL = endpoints.user.addCandidateResume;
    return await formPoster([URL, payload, headers])
}

/**
 * Sets the default resume for a user.
 * @param {Object} payload - The payload containing the information necessary to set the default resume.
 * @returns {Promise} - A promise that resolves with the server response after setting the default resume.
 */

export async function setDefaultResume(payload) {
    const URL = endpoints.user.setDefaultResume
    return await poster([URL, payload])
}

/**
 * Deletes a resume from the server.
 * @param {Object} payload - The payload containing the information necessary to delete a resume.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the resume.
 */
export async function deleteResume(payload) {
    const URL = endpoints.user.deleteResume;
    return await poster([URL, payload])
}

/**
 * Adds the job preferences for a user.
 * @param {Object} payload - The payload containing the job preferences to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the job preferences.
 */
export async function addJobPreferance(payload) {
    const URL = endpoints.user.addJobPreferance;
    return await poster([URL, payload])
}

/**
 * Retrieves the job preferences for a user.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the job preferences.
 */
export async function getJobPreferanceDetails(payload) {
    const URL = endpoints.user.getJobPreferanceDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Uploads the profile picture of a user.
 * @param {Object} payload - The object containing the file to upload.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server after uploading the profile picture.
 */
export async function uploadProfilePic(payload, headers) {
    const URL = endpoints.user.uploadProfilePic;
    return await formPoster([URL, payload, headers])
}
/**
 * Handles the upload data for a user.
 * @param {Object} payload - The object containing the data to upload.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server after handling the upload data.
 */
export async function handleUploadData(payload, headers) {
    const URL = endpoints.user.handleUploadData;
    return await formPoster([URL, payload, headers])
}
/**
 * Uploads resume data for a user.
 * @param {Object} payload - The object containing the resume data to upload.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server after uploading the resume data.
 */
export async function uploadResumeData(payload, headers) {
    const URL = endpoints.user.uploadResumeData;
    return await formPoster([URL, payload, headers])
}

/**
 * Retrieves all education details of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing all education details.
 */

export async function getAllEducationDetails(payload) {
    const URL = endpoints.user.getAllEducationDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the URL of the resume for a user.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the URL of the resume.
 */
export async function getResumeUrl(payload) {
    const URL = endpoints.user.getResumeUrl;
    return await fetcher([URL, { params: payload }])
}
/**
 * Updates the URL of the resume for a user.
 * @param {Object} payload - The payload containing the information necessary to update the resume URL.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the resume URL.
 */
export async function updateResumeUrl(payload) {
    const URL = endpoints.user.updateResumeUrl;
    return await formPoster([URL, payload])
}

/**
 * Retrieves all certification details of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing all certification details.
 */
export async function getAllCertificationDetails(payload) {
    const URL = endpoints.user.getAllCertificationDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves a specific education detail of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the education detail to fetch.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getEducationDetails(payload) {
    const URL = endpoints.user.getEducationDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves a specific certification detail of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the certification detail to fetch.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCertificationDetails(payload) {
    const URL = endpoints.user.getCertificationDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves all work history details of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing all work history details.
 */
export async function getWorkHistoryData(payload) {
    const URL = endpoints.user.getWorkHistoryData;
    return await fetcher([URL, { params: payload }])
}

/**
 * Updates a candidate's certificate details.
 * @param {Object} payload - The object containing the updated certificate details.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the certificate.
 */
export async function updateCandidateCertificate(payload) {
    const URL = endpoints.user.updateCandidateCertificate;
    return await poster([URL, payload])
}

/**
 * Updates a candidate's education details.
 * @param {Object} payload - The object containing the updated education details.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the education details.
 */
export async function updateCandidateEducation(payload) {
    const URL = endpoints.user.updateCandidateEducation;
    return await poster([URL, payload])
}
/**
 * Updates a candidate's work history details.
 * @param {Object} payload - The object containing the updated work history details.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the work history.
 */

export async function updateCandidateWorkHistory(payload) {
    const URL = endpoints.user.updateCandidateWorkHistory;
    return await poster([URL, payload])
}
/**
 * Updates a candidate's custom total work experience.
 * @param {Object} payload - The object containing the updated custom total work experience.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the custom total work experience.
 */
export async function updateCustomTotalWorkExperience(payload) {
    const URL = endpoints.user.updateCustomTotalWorkExperience;
    return await poster([URL, payload])
}

/**
 * Adds expertise and skills to a candidate's profile.
 * @param {Object} payload - The object containing the expertise and skills to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the expertise and skills.
 */
export async function addCandidateExpertiesAndSkills(payload) {
    const URL = endpoints.user.addCandidateExpertiesAndSkills;
    return await poster([URL, payload])
}

/**
 * Retrieves a candidate's expertise and skills details.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the expertise and skills details.
 */
export async function getExpertiseAndSkillsDetails(payload) {
    const URL = endpoints.user.getExpertiseAndSkillsDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Updates the star details of a candidate's expertise and skills.
 * @param {Object} payload - The object containing the updated star details.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the star details.
 */

export async function updateStarDetails(payload) {
    const URL = endpoints.user.updateStarDetails;
    return await poster([URL, payload])
}

/**
 * Deletes expertise and skills from a candidate's profile.
 * @param {Object} payload - The object containing the expertise and skills to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the expertise and skills.
 */
export async function deleteExpertiseAndSkills(payload) {
    const URL = endpoints.user.deleteExpertiseAndSkills;
    return await poster([URL, payload])
}
/**
 * Deletes education history from a candidate's profile.
 * @param {Object} payload - The object containing the education history to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the education history.
 */
export async function deleteEducationHistory(payload) {
    const URL = endpoints.user.deleteEducationHistory;
    return await poster([URL, payload])
}
/**
 * Deletes work history from a candidate's profile.
 * @param {Object} payload - The object containing the work history to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the work history.
 */
export async function deleteWorkHistory(payload) {
    const URL = endpoints.user.deleteWorkHistory;
    return await poster([URL, payload])
}
/**
 * Deletes a certificate history from a candidate's profile.
 * @param {Object} payload - The object containing the certificate history to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the certificate history.
 */
export async function deleteCertificateHistory(payload) {
    const URL = endpoints.user.deleteCertificateHistory;
    return await poster([URL, payload])
}


/**
 * Resets the password for a user using the provided payload.
 * @param {Object} payload - The payload containing the user's email address and new password.
 * @returns {Promise} - A promise that resolves with the password reset response.
 */
export async function passwordReset(payload) {
    const URL = endpoints.user.passwordReset;
    return await poster([URL, payload])
};

/**
 * Sets the job search status for a user.
 * @param {Object} payload - The object containing the user's ID and the job search status to set.
 * @returns {Promise} - A promise that resolves with the response from the server after setting the job search status.
 */

export async function setJobSearchStatus(payload) {
    const URL = endpoints.user.setJobSearchStatus;
    return await poster([URL, payload])
};

/**
 * Retrieves the list of functional domains.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the list of functional domains.
 */
export async function getFunctionalDomain(payload) {
    const URL = endpoints.user.getFunctionalDomain;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the domain expertise details.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the domain expertise details.
 */

export async function getDomainExpertise(payload) {
    const URL = endpoints.user.getDomainExpertise;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the expertise skills of a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the expertise skills.
 */

export async function getExpertiseSkills(payload) {
    const URL = endpoints.user.getExpertiseSkills;
    return await fetcher([URL, { params: payload }])
}

/**
 * Adds a custom section to a candidate's profile.
 * @param {Object} payload - The object containing the custom section details to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the custom section.
 */
export async function addCustomSection(payload) {
    const URL = endpoints.user.addCustomSection;
    return await poster([URL, payload])
}

/**
 * Retrieves a custom section from a candidate's profile.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the custom section.
 */
export async function getCustomSection(payload) {
    const URL = endpoints.user.getCustomSection;
    return await fetcher([URL, { params: payload }])
}

/**
 * Deletes a custom section from a candidate's profile.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the custom section to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the custom section.
 */
export async function deleteCustomSection(payload) {
    const URL = endpoints.user.deleteCustomSection;
    return await poster([URL, payload])
}

/**
 * Adds a summary to a candidate's profile.
 * @param {Object} payload - The object containing the summary details to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the summary.
 */
export async function addSummary(payload) {
    const URL = endpoints.user.addSummary;
    return await poster([URL, payload])
}
/**
 * Updates the finance technologies for a user.
 * @param {Object} payload - The object containing the finance technologies data to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the finance technologies.
 */

export async function updateFinanceTechnologies(payload) {
    const URL = endpoints.user.updateFinanceTechnologies;
    return await poster([URL, payload])
}

/**
 * Updates a user's profile information.
 * @param {Object} payload - The object containing the information to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the user profile.
 */
export async function updateUser(payload) {
    const URL = endpoints.user.updateUser;
    return await poster([URL, payload])
}

/**
 * Retrieves the resume template for a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the resume template.
 */
export async function getTemplate(payload) {
    const URL = endpoints.user.getTemplate;
    return await fetcher([URL, { params: payload }])
}
/**
 * Calculates the work gap for a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the calculated work gap.
 */
export async function calculateWorkGap(payload) {
    const URL = endpoints.user.calculateWorkGap;
    return await fetcher([URL, { params: payload }])
}
/**
 * Sets the resume template for a user.
 * @param {Object} payload - The object containing the information necessary to set the resume template.
 * @returns {Promise} - A promise that resolves with the response from the server after setting the resume template.
 */
export async function setTemplate(payload) {
    const URL = endpoints.user.setTemplate;
    return await poster([URL, payload])
}

/**
 * Creates a duplicate of an existing resume.
 * @param {Object} payload - The object containing the resume ID to duplicate.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function createDuplicateResume(payload) {
    const URL = endpoints.user.createDuplicateResume;
    return await poster([URL, payload])
}

/**
 * Retrieves the resumes data for a user.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the resumes data.
 */

export async function getResumesData(payload) {
    const URL = endpoints.user.getResumesData;
    return await fetcher([URL, { params: payload }])
}
/**
 * Sets the resumes data for a user.
 * @param {Object} payload - The object containing the resumes data to be set.
 * @returns {Promise} - A promise that resolves with the response from the server after setting the resumes data.
 */

export async function setResumesData(payload) {
    const URL = endpoints.user.setResumesData;
    return await poster([URL, payload])
}

/**
 * Updates the resume data for a user.
 * @param {Object} payload - The object containing the resume data to be updated.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the resume data.
 */
export async function updateResumeData(payload) {
    const URL = endpoints.user.updateResumeData;
    return await poster([URL, payload])

}
/**
 * Retrieves the summary of a user's resume.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the summary of the user's resume.
 */
export async function getSummary(payload) {
    const URL = endpoints.user.getSummary;
    return await fetcher([URL, { params: payload }])
}

/**
 * Updates the contact information for a user.
 * @param {Object} payload - The object containing the contact information to be updated.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the contact information.
 */

export async function updateContact(payload) {
    const URL = endpoints.user.updateContact;
    return await poster([URL, payload])
}

/**
 * Sets the contact information for a user.
 * @param {Object} payload - The object containing the contact information to be set.
 * @returns {Promise} - A promise that resolves with the response from the server after setting the contact information.
 */
export async function setContact(payload) {
    const URL = endpoints.user.setContact;
    return await poster([URL, payload])
}

/**
 * Updates a candidate's work experience.
 * @param {Object} payload - The object containing the work experience details to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the work experience.
 */

export async function updateCandidateWorkExperience(payload) {
    const URL = endpoints.user.updateCandidateWorkExperience;
    return await poster([URL, payload])
}

/**
 * Updates the gap in a candidate's work experience.
 * @param {Object} payload - The object containing the gap details to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the work experience gap.
 */
export async function updateCandidateWorkExperienceGap(payload) {
    const URL = endpoints.user.updateCandidateWorkExperienceGap;
    return await poster([URL, payload])
}
/**
 * Updates a candidate's skills.
 * @param {Object} payload - The object containing the skills to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the skills.
 */

export async function updateSkills(payload) {
    const URL = endpoints.user.updateSkills;
    return await poster([URL, payload])
}
/**
 * Deletes skills from a candidate's profile.
 * @param {Object} payload - The object containing the skills to delete.
 * @returns {Promise} - A promise that resolves with the response from the server after deleting the skills.
 */

export async function deleteSkills(payload) {
    const URL = endpoints.user.deleteSkills;
    return await poster([URL, payload])
}
/**
 * Adds a single job preference to a user's profile.
 * @param {Object} payload - The object containing the job preference information to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the job preference.
 */
export async function addSingleJobPreference(payload) {
    const URL = endpoints.user.addSingleJobPreference;
    return await poster([URL, payload])
}

/**
 * Uploads a photo within the resume section.
 * @param {Object} payload - The object containing the photo file to upload.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server after uploading the photo.
 */
export async function uploadPhotoInResume(payload, headers) {
    const URL = endpoints.user.uploadPhotoInResume;
    return await formPoster([URL, payload, headers])
}
/**
 * Uploads a resume within the resume section.
 * @param {Object} payload - The object containing the resume file to upload.
 * @param {Object} headers - The headers to include in the request.
 * @returns {Promise} - A promise that resolves with the response from the server after uploading the resume.
 */

export async function uploadResumeInResume(payload, headers) {
    const URL = endpoints.user.uploadResumeInResume;
    return await formPoster([URL, payload, headers])
}
/**
 * Updates the main resume for a user.
 * @param {Object} payload - The payload containing the resume data to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the main resume.
 */

export async function updateMainResume(payload) {
    const URL = endpoints.user.updateMainResume;
    return await poster([URL, payload])
}

// Old apis

/**
 * Retrieves the list of B2B users for a company.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the list of B2B users.
 */
export async function getB2Busers(payload) {
    const URL = endpoints.user.getB2Busers;
    // return await fetcher([URL, payload])
    return await fetcher([URL + `${""}`])
}

/**
 * Removes a B2B user from the list of B2B users for a company.
 * @param {Object} payload - The payload containing the B2B user ID to remove.
 * @returns {Promise} - A promise that resolves with the response from the server after removing the B2B user.
 */
export async function removeB2BUser(payload) {
    const URL = endpoints.user.getB2Busers;
    return await fetcher([URL, payload])
}

/**
 * Retrieves the associated B2B user for a company.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the associated B2B user.
 */
export async function getB2BAssociatedUser(payload) {
    const URL = endpoints.user.getB2BAssociatedUser;
    return await fetcher([URL, payload])
}

/**
 * Adds an associated B2B user for a company.
 * @param {Object} payload - The payload containing the details of the B2B user to add.
 * @returns {Promise} - A promise that resolves with the response from the server after adding the associated B2B user.
 */

export async function addB2BAssociatedUser(payload) {
    const URL = endpoints.user.addB2BAssociatedUser;
    return await poster([URL, payload])
}

/**
 * Removes an associated B2B user for a company.
 * @param {Object} payload - The payload containing the details of the B2B user to remove.
 * @returns {Promise} - A promise that resolves with the response from the server after removing the associated B2B user.
 */

export async function removeB2BAssociatedUser(payload) {
    const URL = endpoints.user.removeB2BAssociatedUser;
    return await poster([URL, payload])
}

/**
 * Saves the profile of a B2B user.
 * @param {Object} payload - The payload containing the profile data of the B2B user.
 * @returns {Promise} - A promise that resolves with the response from the server after saving the profile.
 */
export async function saveB2BProfile(payload) {
    const URL = endpoints.user.saveB2BProfile
    // return await poster([{URL, body: {companyProfileData : payload} }])
    return await poster([URL, payload])
}

/**
 * Retrieves the list of internal users for a company.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the list of internal users.
 */
export async function getInternalUsers(payload) {
    const URL = endpoints.user.getInternalUsers
    // return await poster([{URL, body: {companyProfileData : payload} }])
    return await fetcher([URL, payload])
}

/**
 * Saves internal users for a company.
 * @param {Object} payload - The payload containing details of the internal users to save.
 * @returns {Promise} - A promise that resolves with the response from the server after saving the internal users.
 */

export async function saveInternalUsers(payload) {
    const URL = endpoints.user.saveInternalUsers
    // return await poster([{URL, body: {companyProfileData : payload} }])
    return await poster([URL, payload])
}

/**
 * Removes internal users for a company.
 * @param {Object} payload - The payload containing the details of the internal users to remove.
 * @returns {Promise} - A promise that resolves with the response from the server after removing the internal users.
 */
export async function removeInternalUsers(payload) {
    const URL = endpoints.user.removeInternalUsers
    // return await poster([{URL, body: {companyProfileData : payload} }])
    return await poster([URL, payload])
}

/**
 * Updates a user's profile information.
 * @param {Object} payload - The object containing the information to update.
 * @returns {Promise} - A promise that resolves with the response from the server after updating the user profile.
 */
export async function updateUserProfile(payload) {
    const URL = endpoints.user.updateUserProfile
    // return await poster([{URL, body: {companyProfileData : payload} }])
    return await poster([URL, payload])
}
/**
 * Retrieves the status of a user's order.
 * @param {Object} payload - The payload containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the order status.
 */
export async function getUserOrderStatus(payload) {
    const URL = endpoints.user.getUserOrder
    return await fetcher([URL, payload])
}

/**
 * Retrieves the PDF of an invoice.
 * @param {Object} payload - The object containing the details required to fetch the invoice PDF.
 * @returns {Promise} - A promise that resolves with the response from the server containing the PDF data.
 */

export async function getInvoicePdf(payload) {
    const URL = endpoints.user.getInvoicePdf
    return await poster([URL, payload])
}

/**
 * Saves the details of an invoice.
 * @param {Object} payload - The object containing the details of the invoice to save.
 * @returns {Promise} - A promise that resolves with the response from the server after saving the invoice details.
 */
export async function saveInvoiceDetails(payload) {
    const URL = endpoints.user.saveInvoiceDetails
    return await poster([URL, payload])
}

/**
 * Retrieves the data for an invoice based on the payload provided.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves with the response from the server containing the invoice data.
 */
export async function fetchInvoiceData(payload) {
    const URL = endpoints.user.fetchInvoiceData
    return await poster([URL, payload])
}

/**
 * Checks if an invoice number already exists.
 * @param {Object} payload - The object containing the invoice number to check.
 * @returns {Promise} - A promise that resolves with the response from the server indicating if the invoice number already exists.
 */
export async function toCheckInvoiceNumber(payload) {
    const URL = endpoints.user.toCheckInvoiceNumber
    return await poster([URL, payload])
} 
